<template>
  <modal :show.sync="show" modalClasses="modal-hide-footer">
    <h3 slot="header" class="mb-0">Bankruptcy</h3>

    <div v-if="_gAuth.isAppSas()">
    <div v-if="tableData.length > 0" class="rounded-bottom">
      <el-table
        :data="tableData"
        style="width: 100%">
        
        <el-table-column
          prop="legal_case_case_type"
          label="Case Type"
          min-width="150"
          width="auto">
          <template slot-scope="scope">
            <p class="text-break top-line">{{ scope.row.legal_case_case_type }}</p>
          </template>
        </el-table-column>

        <el-table-column
          prop="legal_case_plaintiff"
          label="Plaintiff"
          min-width="150"
          width="auto">
          <template slot-scope="scope">
            <p class="text-break top-line">{{ scope.row.legal_case_plaintiff || '-' }}</p>
          </template>
        </el-table-column>

        <el-table-column
          prop="legal_case_subject_comment"
          label="Subject Comment"
          min-width="150"
          width="auto">
          <template slot-scope="scope">
            <p class="text-break top-line">{{ scope.row.legal_case_subject_comment }}</p>
          </template>
        </el-table-column>
      </el-table>
    </div>
      <!-- Empty state if no bankruptcy legal cases -->
        <div v-else class="bg-light p-3 text-center rounded-bottom">
          <p class="text-xs">No Bankruptcy Cases recorded</p>
        </div>
    </div>

     <div v-else>
      <!-- Display bankruptcy legal cases data -->
      <div class="detailed-legal-cases" style="margin-top: 30px;">
        
        <!-- Check if we have bankruptcy legal cases data -->
        <div v-if="hasBankruptcyLegalCases">
          <div v-for="(legalCase, index) in normalizedBankruptcyLegalCases" :key="index" class="case-detail" style="margin-bottom: 30px; page-break-inside: avoid;">
            <!-- Case Header -->
            <div class="case-header" style="background: #f5f5f5; padding: 10px; border-left: 4px solid #ccc;">
              <strong>{{ index + 1 }}. {{ legalCase.title || 'Bankruptcy Case' }}</strong>
            </div>
            
            <!-- Note -->
            <div class="case-note" style="font-style: italic; margin: 10px 0; padding: 10px;  border-radius: 4px;">
              Note: The record below is NOT intended to reflect the 'current status' of the case. Any judgement, order or action where indicated, could have subsequently been settled,
              quashed, rescinded, revoked, set aside, annulled, struck off or dismissed. Please check with the relevant parties.
            </div>

            <!-- Case Details Table -->
              <table class="legal-cases-table bordered" style="width:100%; margin-bottom: 15px;">
                <tbody>
                  <tr>
                    <td style="width: 150px; background: #e9e9e9;"><strong>Special Remark</strong></td>
                    <td colspan="3">{{ safeJoin(legalCase.special_remark) }}</td>
                  </tr>
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Name</strong></td>
                    <td colspan="3">{{ legalCase.name || '-' }}</td>
                  </tr>
                  <tr>
                    <td style="background: #e9e9e9;"><strong>New ID</strong></td>
                    <td colspan="3">{{ legalCase.nic_brno }} ({{ legalCase.nic_brno_match === '1' ? 'NEW ID MATCH' : 'ID MISMATCH' }})</td>
                  </tr>
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Old ID</strong></td>
                    <td colspan="3">{{ safeJoin(legalCase.ic_lcno) }}</td>
                  </tr>
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Remark</strong></td>
                    <td colspan="3">{{ legalCase.remark || '-' }}</td>
                  </tr>
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Address</strong></td>
                    <td colspan="3">{{ legalCase.addr || '-' }}</td>
                  </tr>
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Firm</strong></td>
                    <td colspan="3">{{ safeJoin(legalCase.firm) }}</td>
                  </tr>
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Exparte</strong></td>
                    <td colspan="3">{{ legalCase.exparte || '-' }}</td>
                  </tr>
                  
                  <!-- Case No. and Hearing Date side by side -->
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Case No.</strong></td>
                    <td>{{ legalCase.case_no || '-' }}</td>
                    <td style="background: #e9e9e9;"><strong>Hearing Date</strong></td>
                    <td>{{ legalCase.hear_date || '-' }}</td>
                  </tr>
                  
                  <!-- Notice Date and Gazette side by side -->
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Notice Date</strong></td>
                    <td>
                      <span v-if="legalCase.notice && legalCase.notice.length > 0">
                        {{ safeJoin(legalCase.notice) }}
                      </span>
                      <span v-else>-</span>
                    </td>
                    <td style="background: #e9e9e9;"><strong>Gazette</strong></td>
                    <td>{{ safeJoin(legalCase.gazette_notice) }}</td>
                  </tr>
                  
                  <!-- Petition Date and Gazette side by side -->
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Petition Date</strong></td>
                    <td>
                      <span v-if="legalCase.petition && legalCase.petition.date">
                        {{ legalCase.petition.date }}
                        <span v-if="legalCase.petition.source_detail"> {{ legalCase.petition.source_detail }}</span>
                      </span>
                      <span v-else>-</span>
                    </td>
                    <td style="background: #e9e9e9;"><strong>Gazette</strong></td>
                    <td>{{ safeJoin(legalCase.gazette_petition) }}</td>
                  </tr>
                  
                  <!-- Order Date and Gazette side by side -->
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Order Date</strong></td>
                    <td>
                      <span v-if="legalCase.order && legalCase.order.date">
                        {{ legalCase.order.date }}
                        <span v-if="legalCase.order.source_detail"> {{ legalCase.order.source_detail }}</span>
                      </span>
                      <span v-else>-</span>
                    </td>
                    <td style="background: #e9e9e9;"><strong>Gazette</strong></td>
                    <td>{{ safeJoin(legalCase.gazette_order) }}</td>
                  </tr>
                  
                  <!-- Annulment/Disc and Gazette side by side -->
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Annulment/Disc</strong></td>
                    <td>{{ safeJoin(legalCase.gazette_discharge) }}</td>
                    <td style="background: #e9e9e9;"><strong>Gazette</strong></td>
                    <td>-</td>
                  </tr>
                  
                  <!-- Amount (full width) -->
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Amount</strong></td>
                    <td colspan="3">{{ formatAmount(legalCase.amount) }}</td>
                  </tr>
                  
                  <!-- Solicitor Information -->
                  <tr v-if="legalCase.lawyer && legalCase.lawyer.name">
                    <td style="background: #e9e9e9;"><strong>Solicitor</strong></td>
                    <td colspan="3">{{ legalCase.lawyer.name }}</td>
                  </tr>
                  <tr v-if="legalCase.lawyer && (legalCase.lawyer.add1 || legalCase.lawyer.add2 || legalCase.lawyer.add3)">
                    <td style="background: #e9e9e9;"><strong>Address</strong></td>
                    <td colspan="3">
                      {{ legalCase.lawyer.add1 || '' }} 
                      {{ legalCase.lawyer.add2 || '' }} 
                      {{ legalCase.lawyer.add3 || '' }}
                      {{ legalCase.lawyer.add4 || '' }}
                    </td>
                  </tr>
                  
                  <!-- Tel and Ref side by side -->
                  <tr v-if="legalCase.lawyer && (legalCase.lawyer.tel || legalCase.lawyer.ref)">
                    <td style="background: #e9e9e9;"><strong>Tel</strong></td>
                    <td>{{ legalCase.lawyer.tel || '-' }}</td>
                    <td style="background: #e9e9e9;"><strong>Ref</strong></td>
                    <td>{{ legalCase.lawyer.ref || '-' }}</td>
                  </tr>
                  
                  <!-- Latest Status Information -->
                  <tr v-if="legalCase.latest_status">
                    <td colspan="4" style="background: #f0f0f0; text-align: left;">
                      <strong>SUBJECT'S CONFIRMATION OF STATUS AS OF {{ legalCase.latest_status.date || '-' }}.</strong>
                    </td>
                  </tr>
                  <tr v-if="legalCase.latest_status">
                    <td style="background: #e9e9e9;"><strong>Status</strong></td>
                    <td colspan="3">{{ legalCase.latest_status.code || '-' }}</td>
                  </tr>
                  <tr v-if="legalCase.latest_status">
                    <td colspan="4" style="background: #e9e9e9; text-align: left;">
                      <strong>THIS STATUS WAS UPDATED ON {{ legalCase.latest_status.update_date || '-' }}</strong>
                    </td>
                  </tr>
                  <tr v-if="legalCase.latest_status">
                    <td style="background: #e9e9e9;"><strong>Source Of Update</strong></td>
                    <td>{{ legalCase.latest_status.source || '-' }}</td>
                    <td style="background: #e9e9e9;"><strong>Source Dated</strong></td>
                    <td>{{ legalCase.latest_status.source_date || '-' }}</td>
                  </tr>
                  <tr v-if="legalCase.latest_status">
                    <td style="background: #e9e9e9;"><strong>Next Review Date</strong></td>
                    <td colspan="3">{{ legalCase.latest_status.exp_date || '-' }}</td>
                  </tr>
                </tbody>
              </table>
          </div>
        </div>
        
        <!-- Empty state if no bankruptcy legal cases -->
        <div v-else>
          <!-- Show a detailed table with placeholder values when there's no data -->
          <div class="detailed-legal-cases" style="margin-top: 30px;">
            <h4 style="margin-bottom: 20px;">D1: BANKRUPTCY CASES</h4>
            <div class="case-detail" style="margin-bottom: 30px; page-break-inside: avoid;">
              <div class="case-header" style="background: #f5f5f5; padding: 10px; border-left: 4px solid #ccc;">
                <strong>1. Bankruptcy Case</strong>
              </div>

              <div class="case-note" style="font-style: italic; margin: 10px 0; padding: 10px;  border-radius: 4px;">
                Note: The record below is NOT intended to reflect the 'current status' of the case. Any judgement, order or action where indicated, could have subsequently been settled,
                quashed, rescinded, revoked, set aside, annulled, struck off or dismissed. Please check with the relevant parties.
              </div>

              <table class="legal-cases-table bordered" style="width:100%; margin-bottom: 15px;">
                <tbody>
                  <tr>
                    <td style="width: 150px; background: #e9e9e9;"><strong>Special Remark</strong></td>
                    <td colspan="3">-</td>
                  </tr>
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Name</strong></td>
                    <td colspan="3">-</td>
                  </tr>
                  <tr>
                    <td style="background: #e9e9e9;"><strong>New ID</strong></td>
                    <td colspan="3">-</td>
                  </tr>
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Old ID</strong></td>
                    <td colspan="3">-</td>
                  </tr>
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Remark</strong></td>
                    <td colspan="3">-</td>
                  </tr>
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Address</strong></td>
                    <td colspan="3">-</td>
                  </tr>
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Firm</strong></td>
                    <td colspan="3">-</td>
                  </tr>
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Exparte</strong></td>
                    <td colspan="3">-</td>
                  </tr>
                  <!-- Case No. and Hearing Date -->
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Case No.</strong></td>
                    <td>-</td>
                    <td style="background: #e9e9e9;"><strong>Hearing Date</strong></td>
                    <td>-</td>
                  </tr>
                  <!-- Notice Date and Gazette -->
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Notice Date</strong></td>
                    <td>-</td>
                    <td style="background: #e9e9e9;"><strong>Gazette</strong></td>
                    <td>-</td>
                  </tr>
                  <!-- Petition Date and Gazette -->
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Petition Date</strong></td>
                    <td>-</td>
                    <td style="background: #e9e9e9;"><strong>Gazette</strong></td>
                    <td>-</td>
                  </tr>
                  <!-- Order Date and Gazette -->
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Order Date</strong></td>
                    <td>-</td>
                    <td style="background: #e9e9e9;"><strong>Gazette</strong></td>
                    <td>-</td>
                  </tr>
                  <!-- Annulment/Disc and Gazette -->
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Annulment/Disc</strong></td>
                    <td>-</td>
                    <td style="background: #e9e9e9;"><strong>Gazette</strong></td>
                    <td>-</td>
                  </tr>
                  <!-- Amount (full width) -->
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Amount</strong></td>
                    <td colspan="3">-</td>
                  </tr>
                  <!-- Solicitor Information -->
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Solicitor</strong></td>
                    <td colspan="3">-</td>
                  </tr>
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Address</strong></td>
                    <td colspan="3">-</td>
                  </tr>
                  <!-- Tel and Ref -->
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Tel</strong></td>
                    <td>-</td>
                    <td style="background: #e9e9e9;"><strong>Ref</strong></td>
                    <td>-</td>
                  </tr>
                  <!-- Latest Status (placeholders) -->
                  <tr>
                    <td colspan="4" style="background: #f0f0f0; text-align: left;">
                      <strong>SUBJECT'S CONFIRMATION OF STATUS AS OF -.</strong>
                    </td>
                  </tr>
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Status</strong></td>
                    <td colspan="3">-</td>
                  </tr>
                  <tr>
                    <td colspan="4" style="background: #e9e9e9; text-align: left;">
                      <strong>THIS STATUS WAS UPDATED ON -</strong>
                    </td>
                  </tr>
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Source Of Update</strong></td>
                    <td>-</td>
                    <td style="background: #e9e9e9;"><strong>Source Dated</strong></td>
                    <td>-</td>
                  </tr>
                  <tr>
                    <td style="background: #e9e9e9;"><strong>Next Review Date</strong></td>
                    <td colspan="3">-</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import Modal from "@/components/Modal";
import { Table, TableColumn } from "element-ui";

export default {
  name: "BankruptcyModal",
 props: {
    bankruptcy: {
      type: Array,
      default: () => []
    },
    bankruptcyLegalCases: {
      type: Array,
      default: () => []
    },
    show_bankruptcy_full_view: Boolean
  },
  components: {
    Modal,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn
  },
  data() {
    return {
      tableData: []
    };
  },
  computed: {
    show: {
      get() {
        return this.show_bankruptcy_full_view;
      },
      set(value) {
        this.$emit("update:show_bankruptcy_full_view", value);
      }
    },
       // Check if we have bankruptcy legal cases data
    hasBankruptcyLegalCases() {
      return this.normalizedBankruptcyLegalCases.length > 0;
    },
    
    // Normalize bankruptcy legal cases to always be an array
    normalizedBankruptcyLegalCases() {
      if (!this.bankruptcyLegalCases) return [];
      
      if (Array.isArray(this.bankruptcyLegalCases)) {
        return this.bankruptcyLegalCases;
      } else if (this.bankruptcyLegalCases && typeof this.bankruptcyLegalCases === 'object') {
        // If it's a single object, wrap it in an array
        return [this.bankruptcyLegalCases];
      }
      return [];
    }
  },
  watch: {
    bankruptcy: {
      immediate: true,
      handler(val) {
        this.tableData = val && val.length > 0 ? val : [];
      }
    }
  },
    methods: {
     formatAmount(value) {
      if (value === null || value === undefined || value === '') {
        return '0.00';
      }
      
      // Convert to number and format with 2 decimal places
      const num = parseFloat(value);
      if (isNaN(num)) {
        return '0.00';
      }
      
      return num.toFixed(2);
    },
    safeJoin(array) {
      if (!array || !Array.isArray(array) || array.length === 0) {
        return '-';
      }
      return array.join(', ');
    },
    
    formatReservePrice(price) {
      if (!price) return '0';
      return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
  },
  //  mounted() {
  //      console.log('bankruptcyLegalCases:', this.bankruptcyLegalCases)
  //     //  console.log('Initial akpkSubItems:', this.akpkSubItems)
  //    }
};
</script>
<style>
/* Styles for the plain HTML fallback tables */
.legal-cases-table {
  border-collapse: collapse;
  width: 100%;
  font-family: inherit;
}
.legal-cases-table.bordered,
.legal-cases-table th,
.legal-cases-table td {
  border: 1px solid #000 !important;
}
.legal-cases-table th {
  background-color: #a9a9a9 !important;
  background-image: none !important;
  color: #000 !important;
  font-weight: 700 !important;
  text-align: center;
  padding: 8px 6px;
}
.legal-cases-table td {
  background-color: #fff !important;
  color: #000 !important;
  padding: 8px 6px;
  /* Align body cells to the left for readability */
  text-align: left;
}
.legal-cases-table td:first-child { text-align: center; }
.legal-cases-table .lc-cell { text-align: left; }
.legal-cases-table .text-break { white-space: normal; }
/* Detailed cases styling */
.detailed-legal-cases {
  margin-top: 30px;
}
.case-detail {
  margin-bottom: 30px;
  page-break-inside: avoid;
}
.case-header {
  background: #f5f5f5;
  padding: 10px;
  border-left: 4px solid #ccc;
  font-size: 16px;
}
.case-note {
  font-style: italic;
  margin: 10px 0;
  padding: 10px;
  background: #c1bfb4;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.3;
}
</style>